import { useEffect, useLayoutEffect, useMemo } from "react"
import { useTranslation } from "react-i18next"

import { useActiveOrganization } from "@/contexts/active-organization-context"
import { type SupportedMeasurementSystemCode } from "@/models/measurementSystem"
import { useFlags } from "@/services/feature"
import { useLocalStorage } from "@uidotdev/usehooks"

import {
  CURRENCY_LOCAL_STORAGE_KEY,
  MEASUREMENT_SYSTEM_LOCAL_STORAGE_KEY,
} from "../constants"
import {
  getDefaultMeasurementSystemCode,
  isSupportedMeasurementSystemCode,
} from "../measurement-system"

interface UseSaveAppStateValue {
  saveMeasurementSystem: (
    measurementSystem: SupportedMeasurementSystemCode
  ) => void
  savedMeasurementSystem: SupportedMeasurementSystemCode
}

export const useSaveAppState = (): UseSaveAppStateValue => {
  const { i18n } = useTranslation()
  const flags = useFlags()
  const { activeOrganization } = useActiveOrganization()

  const [savedMeasurementSystem, saveMeasurementSystem] =
    useLocalStorage<SupportedMeasurementSystemCode>(
      MEASUREMENT_SYSTEM_LOCAL_STORAGE_KEY,
      getDefaultMeasurementSystemCode(i18n.language)
    )

  useLayoutEffect(() => {
    localStorage.removeItem(CURRENCY_LOCAL_STORAGE_KEY)
    localStorage.removeItem("unitsSystem")
    localStorage.removeItem("watt-unit")

    let parsedMeasurementSystem: string | null = null

    try {
      const rawMeasurementSystem =
        localStorage.getItem(MEASUREMENT_SYSTEM_LOCAL_STORAGE_KEY) ?? "null"
      parsedMeasurementSystem = JSON.parse(rawMeasurementSystem) as string
    } catch {
      // Invalid JSON: keep parsed as null
    }

    if (
      !isSupportedMeasurementSystemCode(
        parsedMeasurementSystem as SupportedMeasurementSystemCode
      )
    ) {
      saveMeasurementSystem(getDefaultMeasurementSystemCode(i18n.language))
    }
  }, [i18n.language, saveMeasurementSystem])

  // Delete once language feature flag is always on
  // Must wait for activeOrganization to be set
  useEffect(() => {
    if (
      activeOrganization &&
      flags.flagsIsFetched &&
      !flags.user_settings_language
    ) {
      void i18n.changeLanguage("en-US")
    }
  }, [
    activeOrganization,
    flags.flagsIsFetched,
    flags.user_settings_language,
    i18n,
  ])

  // Delete once localization is complete
  // Must wait for activeOrganization to be set
  useEffect(() => {
    if (
      activeOrganization &&
      flags.flagsIsFetched &&
      !flags.user_settings_measurement_system
    ) {
      saveMeasurementSystem("USCS")
    }
  }, [
    activeOrganization,
    flags.flagsIsFetched,
    flags.user_settings_measurement_system,
    saveMeasurementSystem,
  ])

  return useMemo(() => {
    return {
      saveMeasurementSystem,
      savedMeasurementSystem,
    }
  }, [saveMeasurementSystem, savedMeasurementSystem])
}
